import { useEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

/**
 * Robust scroll restoration that survives lazy-loaded content AND
 * height-collapse race conditions.
 *
 * Strategy:
 *  1. Persist the user's scroll position to sessionStorage on every scroll
 *     event (debounced ~150ms). The key is `pathname+search`, so each
 *     unique URL state has its own slot.
 *  2. CRUCIAL: ignore scroll events that fire DURING a route transition.
 *     When React swaps in a shorter page the browser fires scroll(0) — we
 *     don't want that to overwrite the user's actual position.
 *  3. On POP (Back/Forward) we poll up to ~2s for the document to grow tall
 *     enough, then snap to the saved Y. On PUSH/REPLACE we reset to top.
 */
export default function useScrollRestoration() {
  const location = useLocation();
  const navType = useNavigationType();
  const keyRef = useRef(`cartal:scroll:${location.pathname}${location.search}`);
  const inTransitionRef = useRef(false);
  const saveTimerRef = useRef(null);

  // Disable browser's own SPA scroll heuristics once.
  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
  }, []);

  // Single scroll listener for the lifetime of the app. Saves the current Y
  // to sessionStorage under the CURRENT key (read live from the ref).
  useEffect(() => {
    const onScroll = () => {
      if (inTransitionRef.current) return;
      if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
      saveTimerRef.current = window.setTimeout(() => {
        try {
          const y = Math.round(window.scrollY || 0);
          sessionStorage.setItem(keyRef.current, String(y));
        } catch { /* sessionStorage unavailable */ }
      }, 150);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    const onBeforeUnload = () => {
      if (inTransitionRef.current) return;
      try {
        sessionStorage.setItem(keyRef.current, String(Math.round(window.scrollY || 0)));
      } catch { /* ignore */ }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, []);

  // On route change: enter transition mode (ignore scroll), then either
  // restore (POP) or reset to top (PUSH/REPLACE).
  useEffect(() => {
    const newKey = `cartal:scroll:${location.pathname}${location.search}`;
    inTransitionRef.current = true;

    if (navType === "POP") {
      const target = Number(sessionStorage.getItem(newKey) || 0);
      keyRef.current = newKey;
      if (!target || target < 50) {
        inTransitionRef.current = false;
        return;
      }
      let attempts = 0;
      const MAX_ATTEMPTS = 30;
      let lastH = 0;
      let stable = 0;

      const tryRestore = () => {
        attempts += 1;
        const maxScroll = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
        if (maxScroll >= target) {
          window.scrollTo({ top: target, left: 0, behavior: "auto" });
          // Give the page a moment to settle, then resume saving.
          window.setTimeout(() => { inTransitionRef.current = false; }, 200);
          return;
        }
        window.scrollTo({ top: maxScroll, left: 0, behavior: "auto" });
        if (lastH === document.documentElement.scrollHeight) stable += 1;
        else { stable = 0; lastH = document.documentElement.scrollHeight; }
        if (attempts >= MAX_ATTEMPTS || stable >= 3) {
          window.setTimeout(() => { inTransitionRef.current = false; }, 200);
          return;
        }
        window.setTimeout(tryRestore, 80);
      };
      requestAnimationFrame(tryRestore);
    } else {
      // Fresh navigation — start at top (unless hash anchor) and switch key.
      keyRef.current = newKey;
      if (!location.hash) window.scrollTo(0, 0);
      // Resume saving after the next paint so collapse-scroll events are ignored.
      window.setTimeout(() => { inTransitionRef.current = false; }, 200);
    }
  }, [location.pathname, location.search, location.hash, navType]);
}
